// ====================================================
// src/components/block/liveStreamManagement/useTbarTransition.ts
//
// Pilotage temporel du T-bar, sur le modèle d'un vrai mélangeur vidéo : le
// curseur ne revient JAMAIS tout seul au repos. Il reste en bout de course
// (`baseline`, 0 = bas ou 1 = haut) après chaque prise, et c'est le SENS de
// la prochaine prise qui s'inverse — comme sur un T-bar matériel, où on le
// repousse depuis là où on l'a laissé.
//
//   - barValue    : valeur PHYSIQUE 0..1 « de repos », mise à jour par React
//                   state mais VOLONTAIREMENT throttlée (cf. plus bas).
//   - barValueRef : la même valeur, à jour à CHAQUE frame sans passer par
//                   React. TBarFader s'y accroche pour s'animer lui-même en
//                   écrivant le DOM directement (cf. son useEffect interne) —
//                   sinon, un setState à 60fps dans un arbre aussi gros que
//                   LiveStreamManager se fait absorber par React (rendu plus
//                   lent que l'intervalle entre deux frames) et le curseur
//                   saute directement du départ à l'arrivée, sans image
//                   intermédiaire. Même principe que le canvas de régie
//                   (useCanvasPipeline.ts), qui n'utilise que des refs pour
//                   sa boucle 25fps.
//   - scrub(v)  : drag manuel — v est la position physique de la main.
//   - triggerAuto() : rampe automatique depuis la position courante vers
//                 l'extrémité opposée à `baseline`, à la vitesse `speedSec`
//                 (durée en secondes pour une course pleine ; proportionnelle
//                 si la course restante est partielle).
//
// En interne, la position physique est convertie en `progress` LOGIQUE 0..1
// (0 = flux final actuel, 1 = preview a pris l'antenne) selon `baseline` —
// c'est ce progress logique, indépendant du sens physique, qui est poussé à
// `onProgress` (cf. Enchainement + useCanvasPipeline). Quand il atteint 1,
// `onCommit()` est appelé une fois, puis `baseline` bascule côté opposé —
// le curseur reste là où il est arrivé, prêt à repartir en sens inverse.
// ====================================================

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseTbarTransitionOptions {
  onProgress: (progress: number) => void;
  onCommit: () => void;
  defaultSpeedSec?: number;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

// Cadence des setState React pour `barValue` — le rendu visuel ne dépend PAS
// de cette valeur (cf. barValueRef), donc pas besoin de suivre les 60fps ici :
// ça ne fait qu'éviter de re-rendre tout l'arbre régie à chaque frame.
const STATE_FLUSH_MS = 150;

export function useTbarTransition({ onProgress, onCommit, defaultSpeedSec = 1.0 }: UseTbarTransitionOptions) {
  const [barValue, setBarValue] = useState(0);
  const [speedSec, setSpeedSec] = useState(defaultSpeedSec);
  const [isAuto, setIsAuto] = useState(false);
  // Miroir React (peu fréquent : une fois par prise) de baselineRef — sert à
  // afficher un % qui reflète la progression LOGIQUE de la transition et non
  // la position physique brute (cf. TBarFader : les repères "100%"/"0%" de
  // la piste et le badge du curseur s'inversent avec le point de repos).
  const [baseline, setBaseline] = useState<0 | 1>(0);

  // 0 = repos en bas, 1 = repos en haut. C'est ce point qui bascule à chaque
  // prise complétée — le curseur, lui, reste physiquement où il est arrivé.
  const baselineRef = useRef<0 | 1>(0);
  const physicalRef = useRef(0);
  const lastFlushRef  = useRef(0);
  const rafRef        = useRef<number | null>(null);
  const onProgressRef = useRef(onProgress);
  const onCommitRef   = useRef(onCommit);
  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);
  useEffect(() => { onCommitRef.current   = onCommit;   }, [onCommit]);

  const stopAuto = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setIsAuto(false);
  }, []);

  // Position physique → progress logique, selon le point de repos courant.
  const toLogical = (physical: number) =>
    baselineRef.current === 0 ? physical : 1 - physical;

  // Applique une position PHYSIQUE (borné 0..1) : met à jour physicalRef à
  // CHAQUE appel (lu en direct par TBarFader, cf. plus haut), pousse le
  // progress logique correspondant, et à l'arrivée à 1 déclenche le commit
  // puis bascule `baseline` — sans jamais ramener le curseur. Le setState
  // React (`barValue`) est throttlé : il ne sert qu'à donner une valeur de
  // repos raisonnable aux consommateurs non animés, jamais à l'affichage
  // temps réel du curseur.
  const applyPhysical = useCallback((physical: number) => {
    const clamped = clamp01(physical);
    physicalRef.current = clamped;

    const logical = toLogical(clamped);
    onProgressRef.current(logical);

    let committed = false;
    if (logical >= 1) {
      onCommitRef.current();
      baselineRef.current = baselineRef.current === 0 ? 1 : 0;
      setBaseline(baselineRef.current);
      // Le curseur reste en `clamped` (= le nouveau baseline) : pas de reset.
      committed = true;
    }

    const now = performance.now();
    if (committed || now - lastFlushRef.current >= STATE_FLUSH_MS) {
      lastFlushRef.current = now;
      setBarValue(clamped);
    }
  }, []);

  const scrub = useCallback((physical: number) => {
    stopAuto();
    applyPhysical(physical);
  }, [stopAuto, applyPhysical]);

  const triggerAuto = useCallback(() => {
    if (rafRef.current !== null) return; // rampe déjà en cours
    const from   = physicalRef.current;
    const target = baselineRef.current === 0 ? 1 : 0;
    const distance = Math.abs(target - from);
    if (distance === 0) { applyPhysical(target); return; }

    const fullDurationMs = Math.max(0.05, speedSec) * 1000;
    const durationMs     = fullDurationMs * distance;
    const start = performance.now();
    setIsAuto(true);

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      applyPhysical(from + (target - from) * t);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        setIsAuto(false);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [applyPhysical, speedSec]);

  useEffect(() => () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); }, []);

  return { barValue, barValueRef: physicalRef, baseline, isAuto, speedSec, setSpeedSec, scrub, triggerAuto, stopAuto };
}
